import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card, Icon, PageHeader, Table } from '../../design';
import { fmtDate, fmtRelative } from '../../lib/format';
import { ALERT_TYPE, type Alert } from '../alerts/AlertsPage';
import {
  type Locker,
  type LockerStatus,
  STATUS,
  STATUS_COLOR,
  StatusBadge,
  TYPE_LABEL,
} from '../lockers/common';
import { type OperationStatus, useCount } from '../operations/common';
import { Kpi } from '../operations/OperationsPage';
import { OperationWizard } from '../operations/OperationWizard';
import os from '../operations/operations.module.css';
import { ActivityChart, activityDays } from './ActivityChart';
import s from './dashboard.module.css';

const PRIORITY: Record<LockerStatus, number> = {
  offline: 0,
  degraded: 1,
  maintenance: 2,
  online: 3,
  unprovisioned: 4,
};

function countOps(status: OperationStatus) {
  return () =>
    unwrap(api.GET('/operations', { params: { query: { status, pageSize: 1 } } })) as Promise<{
      total: number;
    }>;
}

/** Prezentarea generală (D10): totul agregat în UI din /lockers, /operations și /alerts. */
export function DashboardPage() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const canOps = has('operations');
  const canAlerts = has('alerts');

  const lockers = useQuery({
    queryKey: ['lockers', 'dashboard'],
    queryFn: () => unwrap(api.GET('/lockers', { params: { query: { pageSize: 100 } } })),
  });
  const pending = useCount(['operations', 'pending'], countOps('pending'));
  const ready = useCount(['operations', 'ready'], countOps('ready'));
  const expired = useCount(['operations', 'expired'], countOps('expired'));
  const recentOps = useQuery({
    queryKey: ['operations', 'dashboard'],
    queryFn: () => unwrap(api.GET('/operations', { params: { query: { pageSize: 100 } } })),
    enabled: canOps,
  });
  const alertsNew = useQuery({
    queryKey: ['alerts', 'dashboard', 'new'],
    queryFn: () =>
      unwrap(api.GET('/alerts', { params: { query: { status: 'new', pageSize: 25 } } })),
    enabled: canAlerts,
  });
  const alertsAck = useQuery({
    queryKey: ['alerts', 'dashboard', 'ack'],
    queryFn: () =>
      unwrap(api.GET('/alerts', { params: { query: { status: 'ack', pageSize: 25 } } })),
    enabled: canAlerts,
  });

  const fleet = useMemo(() => lockers.data?.items ?? [], [lockers.data]);
  const [now] = useState(() => new Date());
  const byStatus = useMemo(() => {
    const c: Record<LockerStatus, number> = {
      online: 0,
      degraded: 0,
      offline: 0,
      maintenance: 0,
      unprovisioned: 0,
    };
    for (const l of fleet) c[l.status]++;
    return c;
  }, [fleet]);
  const total = fleet.length;
  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);
  const ring = `conic-gradient(${STATUS_COLOR.online} 0 ${pct(byStatus.online)}%, ${STATUS_COLOR.degraded} ${pct(byStatus.online)}% ${pct(byStatus.online + byStatus.degraded)}%, ${STATUS_COLOR.offline} ${pct(byStatus.online + byStatus.degraded)}% ${pct(byStatus.online + byStatus.degraded + byStatus.offline)}%, ${STATUS_COLOR.maintenance} ${pct(byStatus.online + byStatus.degraded + byStatus.offline)}% ${pct(total - byStatus.unprovisioned)}%, ${STATUS_COLOR.unprovisioned} ${pct(total - byStatus.unprovisioned)}% 100%)`;
  const priority = useMemo(
    () =>
      fleet
        .slice()
        .sort((a, b) => PRIORITY[a.status] - PRIORITY[b.status])
        .slice(0, 4),
    [fleet],
  );

  const openAlerts: Alert[] = useMemo(
    () =>
      [...(alertsNew.data?.items ?? []), ...(alertsAck.data?.items ?? [])].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      ),
    [alertsNew.data, alertsAck.data],
  );
  const openTotal =
    alertsNew.data && alertsAck.data ? alertsNew.data.total + alertsAck.data.total : undefined;
  const critical = openAlerts.filter((a) => a.level === 'critical').length;
  const days = useMemo(
    () => activityDays(recentOps.data?.items ?? [], 7, now),
    [recentOps.data, now],
  );
  const sum = (k: 'created' | 'done') => days.reduce((a, d) => a + d[k], 0);
  const waiting =
    ready.total === undefined || expired.total === undefined
      ? undefined
      : ready.total + expired.total;

  return (
    <>
      <PageHeader
        title={`Bună, ${user?.name.split(' ')[0] ?? ''}. Rețeaua ta, sub control.`}
        subtitle="O singură consolă pentru echipamente, acces și operațiuni."
        actions={
          canOps ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              Operațiune nouă
            </Button>
          ) : (
            <Link to="/lockere">
              <Button variant="primary" icon="locker">
                Vezi rețeaua
              </Button>
            </Link>
          )
        }
      />
      <div className={os.kpis}>
        <div className={os.kpi} role="group" aria-label="Lockere în perimetru">
          <span className={os.label}>Lockere în perimetru</span>
          <strong>{lockers.data ? total : '…'}</strong>
          <span>
            <span style={{ color: 'var(--green)' }}>{byStatus.online} online</span> ·{' '}
            {byStatus.degraded} degradat · {byStatus.offline} offline
          </span>
        </div>
        {canOps && (
          <Kpi label="Așteaptă încărcare" value={pending.total} hint="Chei de depunere emise" />
        )}
        {canOps && (
          <div className={os.kpi} role="group" aria-label="În așteptare de ridicare">
            <span className={os.label}>În așteptare de ridicare</span>
            <strong>{waiting ?? '…'}</strong>
            <span style={expired.total ? { color: 'var(--orange)' } : undefined}>
              {expired.total ?? '…'} cu termen depășit
            </span>
          </div>
        )}
        {canAlerts && (
          <div className={os.kpi} role="group" aria-label="Alerte deschise">
            <span className={os.label}>Alerte deschise</span>
            <strong style={critical ? { color: 'var(--red)' } : undefined}>
              {openTotal ?? '…'}
            </strong>
            <span>
              {critical} critice · {alertsNew.data?.total ?? '…'} de preluat
            </span>
          </div>
        )}
      </div>

      <div className={s.cols}>
        {canOps && (
          <Card>
            <div className="flex between">
              <div>
                <h2>Activitatea rețelei</h2>
                <p className="small muted">
                  Operațiuni create și finalizate · ultimele 7 zile (din cele mai recente 100)
                </p>
              </div>
              <div className={s.legend}>
                <span>
                  <i style={{ background: '#3878df' }} />
                  Create
                </span>
                <span>
                  <i style={{ background: '#bccfeb' }} />
                  Finalizate
                </span>
              </div>
            </div>
            <div className={s.stats}>
              <div>
                <strong>{recentOps.data ? sum('created') : '…'}</strong>
                <span>operațiuni create</span>
              </div>
              <div>
                <strong>{recentOps.data ? sum('done') : '…'}</strong>
                <span>operațiuni finalizate</span>
              </div>
              <div>
                <strong>{recentOps.data ? (days[days.length - 1]?.created ?? 0) : '…'}</strong>
                <span>create astăzi</span>
              </div>
            </div>
            <ActivityChart days={days} />
            <div className={s.foot}>
              <span>
                {fmtDate(new Date(now.getTime() - 6 * 86_400_000).toISOString())} –{' '}
                {fmtDate(now.toISOString())}
              </span>
              <Link to="/operatiuni">Toate operațiunile</Link>
            </div>
            {recentOps.error && (
              <p style={{ color: 'var(--red)' }}>{errorMessage(recentOps.error)}</p>
            )}
          </Card>
        )}
        {canAlerts && (
          <Card>
            <div className="flex between">
              <div>
                <h2>Necesită atenție</h2>
                <p className="small muted">Incidente active în perimetrul tău</p>
              </div>
              <Badge tone={openTotal ? 'orange' : 'green'}>{openTotal ?? '…'} active</Badge>
            </div>
            <div style={{ marginTop: 8 }}>
              {openAlerts.slice(0, 3).map((a) => (
                <div key={a.id} className={s.alertRow}>
                  <span
                    className={[s.symbol, a.level === 'critical' && s.bad]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <Icon name="alert" size={18} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3>{a.title}</h3>
                    <p>
                      {ALERT_TYPE[a.type]} · {fleet.find((l) => l.id === a.lockerId)?.name ?? '…'}
                    </p>
                  </div>
                  <span className={s.time}>{fmtRelative(a.createdAt)}</span>
                </div>
              ))}
              {alertsNew.data && alertsAck.data && openAlerts.length === 0 && (
                <div className="flex" style={{ padding: '14px 0', color: 'var(--green)' }}>
                  <Icon name="shield" size={18} /> Niciun incident activ.
                </div>
              )}
            </div>
            <div className={s.foot}>
              <span>{alertsAck.data?.total ?? '…'} în lucru</span>
              <Link to="/alerte">Toate alertele</Link>
            </div>
          </Card>
        )}
      </div>

      <div className={s.cols}>
        <Card tight>
          <div className="flex between" style={{ padding: '16px 16px 8px' }}>
            <div>
              <h2>Lockere prioritare</h2>
              <p className="small muted">Conectivitate și disponibilitate operațională</p>
            </div>
            <Link to="/lockere">Toate lockerele</Link>
          </div>
          <Table<Locker>
            columns={[
              {
                key: 'name',
                header: 'Locker',
                render: (l) => (
                  <>
                    <strong>{l.name}</strong>
                    <div className="small muted">
                      {TYPE_LABEL[l.type]} · {l.location.city}
                    </div>
                  </>
                ),
              },
              { key: 'status', header: 'Stare', render: (l) => <StatusBadge status={l.status} /> },
              {
                key: 'version',
                header: 'Configurație',
                render: (l) =>
                  l.configVersion === l.deployedVersion ? (
                    <span className="muted">v{l.deployedVersion}</span>
                  ) : (
                    <Badge tone="orange">v{l.configVersion} nepublicată</Badge>
                  ),
              },
              {
                key: 'seen',
                header: 'Ultimul contact',
                render: (l) => <span className="muted">{fmtRelative(l.lastSeenAt)}</span>,
              },
            ]}
            rows={priority}
            rowKey={(l) => l.id}
            onRowClick={(l) => navigate(`/lockere/${l.id}`)}
            empty={
              lockers.isLoading
                ? 'Se încarcă…'
                : lockers.error
                  ? errorMessage(lockers.error)
                  : 'Niciun locker în perimetru.'
            }
          />
        </Card>
        <Card>
          <h2>Sănătatea echipamentelor</h2>
          <div className={s.donutWrap} style={{ marginTop: 14 }}>
            <div className={s.donut} style={{ background: total ? ring : 'var(--line)' }}>
              <div>
                <b>{lockers.data ? total : '…'}</b>
                <span>lockere</span>
              </div>
            </div>
            <div className={s.statusList}>
              {(Object.keys(STATUS) as LockerStatus[]).map((st) => (
                <div key={st}>
                  <i className={s.dot} style={{ background: STATUS_COLOR[st] }} />
                  <span>{STATUS[st].label}</span>
                  <strong>{byStatus[st]}</strong>
                </div>
              ))}
            </div>
          </div>
          <div className={s.foot}>
            <span>Din heartbeat-urile echipamentelor</span>
            <Link to="/harta">Harta rețelei</Link>
          </div>
        </Card>
      </div>
      {creating && <OperationWizard onClose={() => setCreating(false)} />}
    </>
  );
}
